<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    {{ $tc('LBL_WHOLESALER_BILLS', 2) }}
                </h1>
                <!-- Breadcrumb -->
                <nav
                    class="text-sm text-gray-500 mb-6"
                    aria-label="Breadcrumb"
                >
                    <GoBack />
                </nav>
            </div>
            <!-- breadcrumb end -->

            <div
                class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-visible"
            >
                <!-- Search & Action Bar -->
                <div class="flex flex-wrap items-center justify-between gap-4 p-4 sm:flex-nowrap">
                    <!-- Search Box -->
                    <div class="relative w-full sm:w-auto">
                        <label
                            for="table-search"
                            class="sr-only"
                            >Search</label
                        >
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                                class="w-5 h-5 text-gray-500 dark:text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <t-input
                            id="user-search-box"
                            v-model="search"
                            type="text"
                            variant="success"
                            class="w-full sm:w-80 pl-10 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            placeholder="Search for bill"
                            @keypress.enter="fetchPageData()"
                        />
                    </div>
                </div>

                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                >
                    <div class="overflow-x-auto">
                        <table
                            class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                <tr>
                                    <th
                                        v-if="isAdmin"
                                        class="px-4 py-3"
                                    >
                                        WHOLESALER
                                    </th>
                                    <th class="px-4 py-3">RETAILER</th>
                                    <th class="px-4 py-3">PRODUCT COUNT</th>
                                    <th class="px-4 py-3">QUANTITY</th>
                                    <th class="px-4 py-3">AMOUNT</th>
                                    <th class="px-4 py-3">PAYMENT</th>
                                    <th class="px-4 py-3">STATUS</th>
                                    <th class="px-4 py-3">ORDER PLACED ON</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(group, groupLabel) in groupedBills1">
                                    <tr
                                        v-if="group.length"
                                        class="bg-gray-200 dark:bg-gray-700"
                                    >
                                        <td
                                            :colspan="isAdmin ? 8 : 7"
                                            class="px-4 py-3 font-semibold"
                                        >
                                            <div class="flex justify-between items-center w-full">
                                                <span>{{ groupLabel }}</span>
                                            </div>
                                        </td>
                                    </tr>

                                    <tr
                                        v-if="group.length"
                                        v-for="bill in group"
                                        :key="bill.id"
                                        :class="[
                                            'border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer',
                                            bill.delivery_status === 'Placed'
                                                ? 'bg-red-50 dark:bg-red-900/30'
                                                : bill.delivery_status === 'Packed'
                                                ? 'bg-yellow-50 dark:bg-yellow-900/30'
                                                : bill.delivery_status === 'Shipped'
                                                ? 'bg-blue-50 dark:bg-blue-900/30'
                                                : bill.delivery_status === 'Delivered'
                                                ? 'bg-green-50 dark:bg-green-900/30'
                                                : '',
                                        ]"
                                        @click="goto('account.show', { orderId: bill.bill_number })"
                                    >
                                        <td
                                            v-if="isAdmin"
                                            class="px-4 py-3"
                                        >
                                            {{ bill.wholesaler?.name }}
                                        </td>
                                        <td class="px-4 py-3">{{ bill.user?.name }}</td>
                                        <td class="px-4 py-3">{{ bill.transactions.length }}</td>
                                        <td class="px-4 py-3">{{ getTotalQuantity(bill.transactions) }}</td>
                                        <td class="px-4 py-3 font-semibold text-green-600 dark:text-green-400">
                                            {{ config(common.currency) }}
                                            {{ sumAmountFromTransactions(bill.transactions) }}
                                        </td>
                                        <td class="px-4 py-3">
                                            <div
                                                :class="
                                                    hasPendingUserPayments(bill)
                                                        ? 'bg-red-400'
                                                        : bill.payment_status === 'Paid'
                                                        ? 'bg-green-400'
                                                        : 'bg-yellow-400'
                                                "
                                                class="inline-block px-2 py-1 mt-1 text-sm text-white rounded"
                                            >
                                                {{ hasPendingUserPayments(bill) ? 'Declared' : bill.payment_status }}
                                            </div>
                                        </td>
                                        <td class="px-4 py-3">
                                            <span
                                                :class="{
                                                    'bg-red-400': bill.delivery_status === 'Placed',
                                                    'bg-yellow-400': bill.delivery_status === 'Packed',
                                                    'bg-blue-400': bill.delivery_status === 'Shipped',
                                                    'bg-green-400': bill.delivery_status === 'Delivered',
                                                }"
                                                class="inline-block px-2 py-1 mt-1 text-sm text-white rounded"
                                            >
                                                {{ bill.delivery_status }}
                                            </span>
                                        </td>
                                        <td class="px-4 py-3">{{ bill.created_at | dateLabel }}</td>
                                    </tr>
                                </template>
                                <tr
                                    v-if="!bills.length"
                                    class="text-center"
                                >
                                    <td
                                        colspan="7"
                                        class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                    >
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div
                        v-if="bills.length"
                        class="flex justify-center items-center bg-gray-100 dark:bg-gray-800 px-4 py-4 border-t dark:border-gray-700"
                    >
                        <t-pagination
                            v-model="currentPage"
                            variant="rounded"
                            :total-items="totalRows"
                            :per-page="perPage"
                            :limit="limit"
                            :disabled="disabled"
                            @change="fetchPageData"
                        />
                    </div>
                    <p class="sm:hidden text-xs text-center text-gray-500 py-2">
                        ↔ Swipe left/right to scroll the table
                    </p>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
    import moment from 'moment';
    import AppLayout from '@/Layouts/AppLayout.vue';
    import GoBack from '@/Components/GoBack.vue';
    import common from '@/common';

    export default {
        name: 'BillsOfWholesaler',

        components: {
            AppLayout,
            GoBack,
        },

        props: {
            bills: {
                type: Array,
                required: true,
            },
        },

        data() {
            return {
                common,
                search: this.oldSearch,
                currentPage: null,
                limit: 5,
                perPage: this.bills.per_page,
                totalRows: this.bills.total,
            };
        },

        watch: {
            search: {
                handler() {
                    this.debounceFetchSearchResults();
                },
            },
        },

        mounted() {
            this.debounceFetchSearchResults = _.debounce(this.fetchSearchResults, 1000);
            document.getElementById('user-search-box').focus();
        },

        created() {
            this.currentPage = this.bills.current_page;
        },

        computed: {
            disabled() {
                return !this.bills.length;
            },
            groupedBills1() {
                const groups = {
                    Today: [],
                    Yesterday: [],
                    'This Week': [],
                    'Last Week': [],
                    Past: [],
                };

                const today = moment();
                const yesterday = moment().subtract(1, 'day');
                const startOfWeek = moment().startOf('week');
                const startOfLastWeek = moment().subtract(1, 'week').startOf('week');
                const endOfLastWeek = moment(startOfWeek).subtract(1, 'day');

                for (const order of this.bills || []) {
                    const billDate = moment(order.updated_at);

                    if (billDate.isSame(today, 'day')) {
                        groups['Today'].push(order);
                    } else if (billDate.isSame(yesterday, 'day')) {
                        groups['Yesterday'].push(order);
                    } else if (billDate.isSameOrAfter(startOfWeek)) {
                        groups['This Week'].push(order);
                    } else if (billDate.isBetween(startOfLastWeek, endOfLastWeek, 'day', '[]')) {
                        groups['Last Week'].push(order);
                    } else {
                        groups['Past'].push(order);
                    }
                }

                return groups;
            },
        },

        methods: {
            sumAmountFromTransactions(transactions) {
                let sumGrandTotal = 0;
                transactions.forEach((transaction) => {
                    sumGrandTotal += parseFloat(transaction.grand_total);
                });
                return sumGrandTotal.toFixed(2);
            },

            groupedBills() {
                const groupedBills = [
                    [], // today
                    [], // yesterday
                    [], // thisWeek
                    [], // lastWeek
                    [], // past
                ];

                const today = moment();
                const yesterday = moment().subtract(1, 'day');

                this.bills.forEach((bill) => {
                    if (moment(bill.updated_at).isSame(today, 'day')) {
                        groupedBills[0].push(bill);
                    } else if (moment(bill.updated_at).isSame(yesterday, 'day')) {
                        groupedBills[1].push(bill);
                    } else if (moment(bill.updated_at).week() === moment().week()) {
                        groupedBills[2].push(bill);
                    } else if (moment(bill.updated_at).week() === moment().subtract(1, 'week').week()) {
                        groupedBills[3].push(bill);
                    } else {
                        groupedBills[4].push(bill);
                    }
                });

                return groupedBills;
            },

            getPaymentStatus(bill) {
                return bill.payment_status;
            },

            hasPendingUserPayments(bill) {
                const pendingBills = bill.user_payments.filter(
                    (payment) => payment.accepted_at === null && payment.deleted_at === null,
                );
                return pendingBills.length;
            },

            getTotalQuantity(transactions) {
                let totalQty = 0;
                transactions.forEach((transaction) => {
                    const qty = parseFloat(transaction.qty);
                    totalQty += qty;
                });
                return totalQty;
            },

            fetchSearchResults() {
                this.$inertia.get(
                    route('reports.wholesaler.orders', {
                        wholesaler: this.bills[0].wholesaler.id,
                        page: this.currentPage,
                        search: this.search,
                    }),
                );
            },

            fetchPageData(newPage) {
                this.$inertia.get(
                    route('reports.wholesaler.orders', {
                        wholesaler: this.bills[0].wholesaler.id,
                        page: newPage,
                        search: this.search,
                    }),
                );
            },
        },
    };
</script>
